import { createContext, ReactNode, useContext, useState } from "react";
import { getAccessToken, getIdToken } from "utils/utils";
import { RootStore } from "../stores/RootStore";
import { AuthenticationContext } from "./authentication.context";
import { ErrorBoundary as SystemErrorBoundary } from 'react-error-boundary';
import SystemErrorPage from "views/SystemErrorPage";
import LoadingScreen from "components/Loading/LoadingScreen";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import ErrorPage from "views/ErrorPage";
import useBroadcastChannel from "hooks/useBroadcastChannel";

type RootStoreProviderProps = {
  children: ReactNode;
};

var store: RootStore | null = null;
const StoreContext = createContext<RootStore | undefined>(undefined);

export function useRootStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useRootStore must be used within RootStoreProvider");
  }

  return context;
}

export function useLayoutStore() {
  const { layoutStore } = useRootStore();
  return layoutStore;
}

export function useUserManagementStore() {
  const { userStore } = useRootStore();
  return userStore;
}

export function useWorkgroupsStore() {
  const { workgroupsStore } = useRootStore();
  return workgroupsStore;
}

export function useRolesListStore() {
  const { rolesListStore } = useRootStore();
  return rolesListStore;
}

export function useRoleStore() {
  const { roleStore } = useRootStore();
  return roleStore;
}

export function useSystemAdministratorsStore() {
  const { systemAdministratorsStore } = useRootStore();
  return systemAdministratorsStore;
}

export function useDataTableStore() {
  const { dataTableStore } = useRootStore();
  return dataTableStore;
}

export function useUserRolePermissionStore() {
  const { userRolePermissionStore } = useRootStore();
  return userRolePermissionStore;
}

export function useAssetListStore() {
  const { assetListStore } = useRootStore();
  return assetListStore;
}

export function useAssetStore() {
  const { assetStore } = useRootStore();
  return assetStore;
}

export function useUserListStore() {
  const { userListStore } = useRootStore();
  return userListStore;
}

export function useBeaconlistStore() {
  const { beaconlistStore } = useRootStore();
  return beaconlistStore;
}

export function useBeaconStore() {
  const { beaconStore } = useRootStore();
  return beaconStore;
}

export function useUnregisteredBeaconStore() {
  const { unregisteredBeaconStore } = useRootStore();
  return unregisteredBeaconStore;
}

export function useMapStore() {
  const { mapStore } = useRootStore();
  return mapStore;
}

export function RootStoreProvider({ children }: RootStoreProviderProps) {

  const [loading, setLoading] = useState(!store);
  const [error, setError] = useState("");
  const authContext = useContext(AuthenticationContext).authState;

  RootStore.authToken = getIdToken(authContext);;
  RootStore.accessToken = getAccessToken(authContext);
  const { postBroadcastMessage } = useBroadcastChannel();

  // only create root store once (store is a singleton)
  if (!store) {
    store = new RootStore();
    store?.loadAppData()
      .then(() => setLoading(false))
      .catch((error: ErrorResponseDto) => {
        console.log(error)
        if (error.statusCode === 401)
          postBroadcastMessage('broadcast_signout');
        else
          setError(error.errorMsg)
      })
  }
  const root = store;

  if (loading) {
    if (error !== "") {
      return <ErrorPage message={error} />
    }
    return <LoadingScreen />
  }

  return (
    <SystemErrorBoundary fallback={<SystemErrorPage />}>
      <StoreContext.Provider value={root}>
        {!loading && children}
      </StoreContext.Provider>
    </SystemErrorBoundary>
  )
}